import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../auth/authProvider';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@license-management/ui';
import {
  Key,
  BarChart2,
  User,
  Users,
  UserCircle,
  LogOut,
  FileText,
  Package,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  show: boolean;
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const permissions = usePermissions();
  const [collapsed, setCollapsed] = useState(false);

  const navigation: NavItem[] = [
    {
      name: 'Licenses',
      href: '/licenses',
      icon: Key,
      show: permissions.canViewLicenses(),
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: Users,
      show: permissions.canViewCustomers(),
    },
    {
      name: 'Portal Users',
      href: '/customer-users',
      icon: UserCircle,
      show: permissions.canViewCustomerUsers(),
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChart2,
      show: permissions.canViewAnalytics(),
    },
    {
      name: 'Releases',
      href: '/releases',
      icon: Package,
      show: permissions.canViewReleases(),
    },
    {
      name: 'Users',
      href: '/users',
      icon: User,
      show: permissions.canViewUsers(),
    },
    {
      name: 'Audit Log',
      href: '/audit',
      icon: FileText,
      show: permissions.canViewAudit(),
    },
    {
      name: 'API Keys',
      href: '/api-keys',
      icon: Shield,
      show: permissions.canViewApiKeys(),
    },
  ].filter((item) => item.show);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F5] bg-mesh flex">
      {/* Sidebar — deep Nordic navy */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-[#0A1628] transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-[#152945]">
          <Link to="/" className="flex items-center gap-3 overflow-hidden">
            <img
              src="/icon.png"
              alt="NGA"
              className="flex-shrink-0 w-8 h-8 rounded-lg shadow-glow-primary"
            />
            {!collapsed && (
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-white truncate tracking-wide">
                  NGA Admin
                </span>
                <span className="text-xs text-[#4a6a8a] truncate">
                  License Manager
                </span>
              </div>
            )}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg text-[#4a6a8a] hover:text-white hover:bg-[#152945] transition-colors"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? 'bg-primary-500/15 text-primary-300'
                    : 'text-[#7a9ab8] hover:text-white hover:bg-[#152945]'
                }`}
                title={collapsed ? item.name : undefined}
              >
                <Icon
                  className={`flex-shrink-0 h-4 w-4 transition-transform duration-200 group-hover:scale-110 ${
                    active ? 'text-primary-300' : ''
                  }`}
                />
                {!collapsed && (
                  <span className="truncate tracking-wide">{item.name}</span>
                )}
                {/* Active indicator bar */}
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-400 rounded-r-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-[#152945] p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#7a9ab8] hover:text-white hover:bg-[#152945] transition-colors ${
                  collapsed ? 'justify-center' : ''
                }`}
              >
                <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-[#152945] border border-[#1e3a5f]">
                  <User className="h-4 w-4" />
                </div>
                {!collapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-white truncate text-sm">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-[#4a6a8a] truncate">
                        {user?.email}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-[#4a6a8a]" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={collapsed ? 'center' : 'end'}
              side="top"
              className="w-56"
            >
              <div className="px-3 py-2">
                <p className="text-sm font-semibold text-zinc-900 truncate">
                  {user?.name || user?.email}
                </p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                variant="danger"
                className="cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={`flex-1 transition-all duration-300 ${
          collapsed ? 'ml-16' : 'ml-64'
        }`}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-40 h-16 bg-[#F9F8F5]/90 backdrop-blur-lg border-b border-[#DDD9D1] flex items-center px-6">
          <div className="flex-1">
            {/* Breadcrumb or page title could go here */}
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
